import React, { useState, useEffect } from "react";
import { NavLink } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  Fade,
  Container,
  Stack,
  Chip,
} from "@mui/material";
import {
  QrCodeScanner,
  NetworkWifi,
  TabletAndroid,
  SatelliteAlt,
  ArrowForward,
  StorefrontRounded,
} from "@mui/icons-material";

const COLORS = {
  primary: "#123465",
  secondary: "#f9a836",
  white: "#fff",
};

const slides = [
  {
    badge: "Traçabilité terrain",
    title: "Pilotez vos opérations avec des solutions",
    highlight: "fiables et connectées",
    description:
      "TELEDYNE intègre les équipements, les logiciels et les services qui rendent vos flux plus lisibles, plus rapides et plus faciles à contrôler.",
    icon: <QrCodeScanner />,
    image: "/images/home/hero/tracabilite-rfid.webp",
    metric: "RFID",
    metricLabel: "Inventaires rapides",
    bgPos: { xs: "72% center", md: "center center" },
  },
  {
    badge: "Supply chain & entrepôt",
    title: "Donnez de la visibilité à chaque mouvement",
    highlight: "de stock",
    description:
      "Des solutions robustes pour tracer les articles, équiper les équipes mobiles et réduire les ruptures entre le terrain et vos données.",
    icon: <NetworkWifi />,
    image: "/images/home/hero/supply-chain-entrepot.webp",
    metric: "Temps réel",
    metricLabel: "Décisions plus rapides",
    bgPos: { xs: "76% center", md: "58% center" },
  },
  {
    badge: "Mobilité professionnelle",
    title: "Équipez vos collaborateurs avec du",
    highlight: "matériel durci",
    description:
      "Terminaux, tablettes et scanners conçus pour tenir dans les environnements exigeants, avec un accompagnement local de l’étude au support.",
    icon: <TabletAndroid />,
    image: "/images/home/hero/mobilite-professionnelle.webp",
    metric: "Durci",
    metricLabel: "Matériel terrain",
    bgPos: { xs: "76% center", md: "center center" },
  },
  {
    badge: "Pilotage opérationnel",
    title: "Gardez vos sites synchronisés",
    highlight: "de l’entrepôt au point de vente",
    description:
      "Supervision, intégration et support local pour suivre vos équipements, vos données et vos équipes avec une vision claire de vos opérations.",
    icon: <SatelliteAlt />,
    image: "/images/home/hero/pilotage-operationnel.webp",
    metric: "Pilotage",
    metricLabel: "Opérations synchronisées",
    bgPos: { xs: "76% center", md: "center center" },
  },
  {
    badge: "E-commerce TELEDYNE",
    title: "Commandez vos équipements",
    highlight: "ou demandez un devis en ligne",
    description:
      "Retrouvez une sélection de produits professionnels sur notre boutique en ligne, commandez directement ou transmettez votre besoin pour obtenir un devis adapté.",
    icon: <StorefrontRounded />,
    image: "/images/home/hero/ecommerce.webp",
    metric: "Store",
    metricLabel: "Commande et devis en ligne",
    bgPos: { xs: "64% center", md: "center center" },
    externalLink: "https://store.teledyne.ma",
  },
];

export default function HomeSlider() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    if (isPaused) return;
    const id = setInterval(() => {
      setCurrentSlide((p) => (p + 1) % slides.length);
    }, 5500);
    return () => clearInterval(id);
  }, [isPaused]);

  const handleDotClick = (index) => {
    setCurrentSlide(index);
    setIsPaused(true);
    setTimeout(() => setIsPaused(false), 6000);
  };


  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        height: { xs: "88vh", md: "88vh" },
        minHeight: { xs: 520, md: 600 },
        overflow: "hidden",
      }}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      {slides.map((slide, index) => {
        const active = index === currentSlide;

        return (
          <Fade in={active} appear={false} timeout={900} key={index}>
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                width: "100%",
                height: "100%",
                zIndex: active ? 5 : 1,
                opacity: active ? 1 : 0,
              }}
              aria-current={active ? "true" : "false"}
            >
              {/* Background Image */}
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  backgroundImage: `url(${slide.image})`,
                  backgroundSize: "cover",
                  backgroundRepeat: "no-repeat",
                  backgroundPosition: slide.bgPos || "center",
                  transform: active ? "scale(1)" : "scale(1.06)",
                  transition: "transform 9s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
                }}
              />

              {/* Multi-layer overlay for depth */}
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  background: {
                    xs: "linear-gradient(to top, rgba(5,10,30,0.92) 0%, rgba(5,10,30,0.7) 35%, rgba(5,10,30,0.45) 65%, rgba(5,10,30,0.2) 100%)",
                    sm: "linear-gradient(to top, rgba(5,10,30,0.85) 0%, rgba(5,10,30,0.55) 40%, rgba(5,10,30,0.3) 70%, rgba(5,10,30,0.1) 100%)",
                    md: "linear-gradient(105deg, rgba(5,10,30,0.82) 0%, rgba(5,10,30,0.65) 30%, rgba(5,10,30,0.35) 60%, transparent 100%)",
                  },
                }}
              />

              {/* CONTENT */}
              <Box
                sx={{
                  position: "relative",
                  zIndex: 2,
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  alignItems: { xs: "flex-end", md: "center" },
                  pb: { xs: 10, sm: 12, md: 0 },
                }}
              >
                <Container maxWidth="lg" sx={{ width: "100%" }}>
                  <Box
                    sx={{
                      maxWidth: { xs: "100%", sm: "80%", md: "52%", lg: "48%" },
                    }}
                  >
                    {/* Badge */}
                    <Box
                      sx={{
                        opacity: 0,
                        transform: "translateY(16px)",
                        animation: active ? "hs-fade-up 650ms ease 80ms forwards" : "none",
                        "@keyframes hs-fade-up": {
                          to: { opacity: 1, transform: "translateY(0)" },
                        },
                      }}
                    >
                      <Chip
                        label={slide.badge}
                        icon={React.cloneElement(slide.icon, { sx: { fontSize: '0.9rem !important', color: `${COLORS.primary} !important` } })}
                        sx={{
                          bgcolor: COLORS.secondary,
                          color: COLORS.primary,
                          fontWeight: 800,
                          fontSize: { xs: "0.62rem", sm: "0.68rem", md: "0.72rem" },
                          letterSpacing: 0.6,
                          textTransform: "uppercase",
                          borderRadius: 999,
                          px: { xs: 0.8, sm: 1, md: 1.2 },
                          height: "auto",
                          py: { xs: 1.5, sm: 1.8, md: 2 },
                          "& .MuiChip-label": {
                            padding: { xs: "0 6px", sm: "0 8px" },
                          },
                        }}
                      />
                    </Box>

                    {/* Title */}
                    <Typography
                      variant="h1"
                      component={index === 0 ? "h1" : "h2"}
                      sx={{
                        mt: { xs: 1.5, sm: 2 },
                        color: COLORS.white,
                        fontWeight: 800,
                        letterSpacing: -0.5,
                        lineHeight: 1.12,
                        fontSize: { xs: "1.55rem", sm: "2.1rem", md: "2.7rem", lg: "3.1rem" },
                        textShadow: "0 2px 20px rgba(0,0,0,0.4)",
                        opacity: 0,
                        transform: "translateY(16px)",
                        animation: active ? "hs-fade-up 650ms ease 180ms forwards" : "none",
                      }}
                    >
                      {slide.title}
                      <Box
                        component="span"
                        sx={{
                          display: "block",
                          color: COLORS.secondary,
                          fontWeight: 900,
                          fontSize: "inherit",
                          mt: { xs: 0.3, sm: 0.5, md: 0.6 },
                        }}
                      >
                        {slide.highlight}
                      </Box>
                    </Typography>

                    {/* Divider accent */}
                    <Box
                      sx={{
                        mt: { xs: 1.5, sm: 2 },
                        width: 48,
                        height: 3,
                        borderRadius: 2,
                        bgcolor: COLORS.secondary,
                        opacity: 0,
                        animation: active ? "hs-fade-up 650ms ease 260ms forwards" : "none",
                      }}
                    />

                    {/* Description */}
                    <Typography
                      sx={{
                        mt: { xs: 1.5, sm: 1.75 },
                        color: "rgba(255,255,255,0.88)",
                        fontSize: { xs: "0.88rem", sm: "0.97rem", md: "1.05rem", lg: "1.1rem" },
                        lineHeight: { xs: 1.6, md: 1.7 },
                        maxWidth: { xs: "100%", md: 520 },
                        fontWeight: 400,
                        opacity: 0,
                        transform: "translateY(16px)",
                        animation: active ? "hs-fade-up 650ms ease 320ms forwards" : "none",
                      }}
                    >
                      {slide.description}
                    </Typography>

                    {/* Buttons */}
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={{ xs: 1.2, sm: 1.5 }}
                      sx={{
                        mt: { xs: 2.5, sm: 3 },
                        alignItems: { xs: "stretch", sm: "center" },
                        opacity: 0,
                        transform: "translateY(16px)",
                        animation: active ? "hs-fade-up 650ms ease 400ms forwards" : "none",
                      }}
                    >
                      <Button
                        component={slide.externalLink ? "a" : NavLink}
                        to={slide.externalLink ? undefined : "/solutions"}
                        href={slide.externalLink}
                        target={slide.externalLink ? "_blank" : undefined}
                        rel={slide.externalLink ? "noreferrer" : undefined}
                        onClick={slide.externalLink ? undefined : () => window.scrollTo(0, 0)}
                        variant="contained"
                        endIcon={<ArrowForward sx={{ fontSize: { xs: "1rem", sm: "1.1rem" } }} />}
                        sx={{
                          bgcolor: COLORS.secondary,
                          color: COLORS.primary,
                          fontWeight: 800,
                          fontSize: { xs: "0.88rem", sm: "0.92rem", md: "0.95rem" },
                          borderRadius: "12px",
                          px: { xs: 2.5, sm: 3, md: 3.5 },
                          py: { xs: 1.15, sm: 1.25, md: 1.35 },
                          textTransform: "none",
                          boxShadow: "0 8px 24px rgba(249,168,54,0.35)",
                          transition: "transform .2s ease, box-shadow .2s ease",
                          "&:hover": {
                            bgcolor: "#ffb948",
                            transform: "translateY(-2px)",
                            boxShadow: "0 14px 32px rgba(249,168,54,0.45)",
                          },
                        }}
                      >
                        {slide.externalLink ? "Accéder au store" : "Explorer les solutions"}
                      </Button>

                      <Button
                        component={NavLink}
                        to="/contactus"
                        onClick={() => window.scrollTo(0, 0)}
                        variant="outlined"
                        sx={{
                          borderRadius: "12px",
                          px: { xs: 2.5, sm: 3, md: 3.5 },
                          py: { xs: 1.15, sm: 1.25, md: 1.35 },
                          fontSize: { xs: "0.88rem", sm: "0.92rem", md: "0.95rem" },
                          textTransform: "none",
                          fontWeight: 700,
                          borderWidth: "1.5px",
                          borderColor: "rgba(255,255,255,0.5)",
                          color: COLORS.white,
                          backgroundColor: "rgba(255,255,255,0.08)",
                          backdropFilter: "blur(8px)",
                          "&:hover": {
                            borderWidth: "1.5px",
                            borderColor: "rgba(255,255,255,0.8)",
                            backgroundColor: "rgba(255,255,255,0.15)",
                            color: COLORS.white,
                          },
                        }}
                      >
                        {slide.externalLink ? "Obtenir un devis" : "Parler à un expert"}
                      </Button>
                    </Stack>
                  </Box>
                </Container>
              </Box>
            </Box>
          </Fade>
        );
      })}

      {/* Progress Dots */}
      <Box
        sx={{
          position: "absolute",
          bottom: { xs: 24, sm: 28, md: 44 },
          left: { xs: "50%", md: "50%" },
          transform: "translateX(-50%)",
          display: "flex",
          alignItems: "center",
          gap: { xs: 0.8, sm: 1 },
          zIndex: 20,
        }}
      >
        {slides.map((_, i) => {
          const active = i === currentSlide;
          return (
            <Box
              key={i}
              onClick={() => handleDotClick(i)}
              role="button"
              aria-label={`Aller au slide ${i + 1}`}
              sx={{
                cursor: "pointer",
                width: active ? { xs: 28, sm: 32 } : { xs: 8, sm: 8 },
                height: { xs: 4, sm: 4 },
                borderRadius: "4px",
                bgcolor: active ? COLORS.secondary : "rgba(255,255,255,0.4)",
                transition: "all 0.35s cubic-bezier(0.4,0,0.2,1)",
                boxShadow: active ? "0 0 10px rgba(249,168,54,0.5)" : "none",
                "&:hover": {
                  bgcolor: active ? COLORS.secondary : "rgba(255,255,255,0.65)",
                },
              }}
            />
          );
        })}
      </Box>
    </Box>
  );
}
